import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const tiers = [
  {
    name: '优秀示范',
    color: 'primary' as const,
    bgClass: 'bg-primary-500',
    tagClass: 'bg-primary-100/60 text-primary-700 border border-primary-200/40',
    actions: ['进入案例库', '示范推广'],
    icon: 'ri-medal-line',
  },
  {
    name: '稳定达标',
    color: 'accent' as const,
    bgClass: 'bg-accent-500',
    tagClass: 'bg-gold-100/80 text-gold-700 border border-gold-300/40',
    actions: ['持续跟踪', '保持质量'],
    icon: 'ri-check-double-line',
  },
  {
    name: '重点提升',
    color: 'secondary' as const,
    bgClass: 'bg-secondary-500',
    tagClass: 'bg-secondary-100/60 text-secondary-700 border border-secondary-200/40',
    actions: ['输出改进方案', '配置培训与资源'],
    icon: 'ri-arrow-up-circle-line',
  },
];

export default function P77DataPipeline({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />分档结果
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            把评价转化为<span className="text-gold-600">差异化支持</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            分档不是给教师贴标签，而是让不同课程和教师获得不同类型的支持
          </p>
        </div>

        {/* Three Tier Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {tiers.map((tier, i) => (
            <div
              key={tier.name}
              className="p-6 glass-liquid rounded-xl overflow-hidden relative"
              style={{ transitionDelay: `${300 + i * 150}ms` }}
            >
              <div className={`h-1.5 -mx-6 -mt-6 mb-5 ${tier.bgClass}`} />
              <div className="text-center">
                <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl ${tier.color === 'primary' ? 'bg-primary-100/60 border border-primary-200/40' : tier.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-secondary-100/60 border border-secondary-200/40'} flex items-center justify-center`}>
                  <i className={`${tier.icon} text-2xl ${tier.color === 'primary' ? 'text-primary-600' : tier.color === 'accent' ? 'text-gold-600' : 'text-secondary-600'}`} />
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-800 mb-3">{tier.name}</h3>
                <div className="flex flex-wrap justify-center gap-2">
                  {tier.actions.map((a) => (
                    <span key={a} className={`text-xs font-body font-semibold px-3 py-1.5 rounded-full ${tier.tagClass}`}>
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Special Support */}
        <div className={`max-w-sm mx-auto mb-3 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-3 rounded-xl bg-background-100/50 border border-background-200/50 text-center">
            <span className="text-xs font-body font-bold text-foreground-500">特殊场景另设</span>
            <span className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-body font-semibold bg-background-200/60 text-foreground-600">专项支持</span>
            <span className="ml-2 text-xs font-body text-foreground-500">标签</span>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              课程内部的优秀应当依据本课程专业标准；跨课程的共同问题则依据统一定义的维度。分档服务于<strong className="text-gold-700">资源配置和教学改进</strong>，而不是为了制造简单排名。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}